import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Repeat, Play, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { API } from '../utils/api';

const getYoutubeThumbnail = (url) => {
  if (!url) return null;
  // Extract video ID from various YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return `https://img.youtube.com/vi/${m[1]}/mqdefault.jpg`;
  }
  return null;
};

/**
 * Modal listing substitute exercises fetched from library by ids.
 * Props: open, onClose, exerciseName, substituteIds
 */
export const SubstitutesModal = ({ open, onClose, exerciseName, substituteIds }) => {
  const [subs, setSubs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedVideo, setExpandedVideo] = useState(null);

  useEffect(() => {
    if (!open || !substituteIds || substituteIds.length === 0) {
      setSubs([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/exercise-library-lookup?ids=${substituteIds.join(',')}`)
      .then((res) => {
        if (!cancelled) setSubs(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setSubs([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, substituteIds]);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="w-full max-w-lg bg-zinc-950 border border-neon-lime/20 rounded-3xl shadow-2xl max-h-[85vh] overflow-y-auto"
          initial={{ y: 40, scale: 0.95, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: 40, scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', damping: 24, stiffness: 260 }}
          onClick={(e) => e.stopPropagation()}
          data-testid="substitutes-modal"
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur-md border-b border-white/5 px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-xl bg-neon-lime/15 flex items-center justify-center flex-shrink-0">
                <Repeat className="w-5 h-5 text-neon-lime" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                  Substitutos para
                </p>
                <h3 className="font-primary font-black text-lg text-white truncate">
                  {exerciseName}
                </h3>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white flex items-center justify-center flex-shrink-0 ml-2"
              data-testid="substitutes-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-neon-lime" />
              </div>
            ) : !subs || subs.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-zinc-400 font-bold">Nenhum substituto disponível</p>
                <p className="text-zinc-600 text-sm mt-1">
                  Fale com o professor para cadastrar opções.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {subs.map((sub) => {
                  const thumb = getYoutubeThumbnail(sub.youtube_url);
                  const embedUrl = sub.youtube_url
                    ? sub.youtube_url
                        .replace('watch?v=', 'embed/')
                        .replace('youtu.be/', 'youtube.com/embed/')
                        .replace('/shorts/', '/embed/')
                    : null;
                  const isOpen = expandedVideo === sub.id;
                  return (
                    <div
                      key={sub.id}
                      className="bg-zinc-900/60 border border-white/5 rounded-2xl overflow-hidden"
                      data-testid={`substitute-card-${sub.id}`}
                    >
                      <div className="flex items-stretch gap-3 p-3">
                        {/* Thumbnail */}
                        <div className="w-24 h-16 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0 relative">
                          {thumb ? (
                            <img
                              src={thumb}
                              alt={sub.name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Repeat className="w-6 h-6 text-zinc-700" />
                            </div>
                          )}
                          {sub.youtube_url && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                              <div className="w-8 h-8 rounded-full bg-neon-lime/90 flex items-center justify-center">
                                <Play className="w-4 h-4 text-black fill-black" />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-bold text-sm leading-tight">{sub.name}</p>
                          {sub.category && (
                            <p className="text-zinc-500 text-xs mt-0.5">{sub.category}</p>
                          )}
                          {sub.youtube_url && embedUrl && (
                            <button
                              onClick={() =>
                                setExpandedVideo(isOpen ? null : sub.id)
                              }
                              className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-neon-lime hover:text-white transition-colors"
                              data-testid={`substitute-play-${sub.id}`}
                            >
                              <Play className="w-3 h-3 fill-current" />
                              {isOpen ? 'Fechar' : 'Ver execução'}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Instructions */}
                      {sub.instructions && (
                        <div className="px-3 pb-3">
                          <p className="text-zinc-400 text-xs italic leading-relaxed">
                            {sub.instructions}
                          </p>
                        </div>
                      )}

                      {/* Embed video */}
                      <AnimatePresence>
                        {isOpen && embedUrl && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="px-3 pb-3"
                          >
                            <div className="aspect-video rounded-lg overflow-hidden bg-black border border-neon-lime/20">
                              <iframe
                                src={embedUrl}
                                title={sub.name}
                                className="w-full h-full"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
